import { useSyncExternalStore } from 'react'

export type WidgetDockSide = 'left' | 'right'

export const WIDGET_DOCK_STORAGE_KEY = 'connect-widget-dock-side'

/** Same-tab change notification (the `storage` event only fires in other tabs). */
const WIDGET_DOCK_CHANGE_EVENT = 'connect-widget-dock-side-change'

export function readWidgetDockSide(): WidgetDockSide {
  try {
    const raw = localStorage.getItem(WIDGET_DOCK_STORAGE_KEY)
    if (raw === 'left' || raw === 'right') return raw
  } catch {
    // ignore storage errors
  }
  return 'right'
}

export function writeWidgetDockSide(side: WidgetDockSide): void {
  try {
    localStorage.setItem(WIDGET_DOCK_STORAGE_KEY, side)
  } catch {
    // ignore storage errors
  }
  try {
    window.dispatchEvent(new CustomEvent(WIDGET_DOCK_CHANGE_EVENT, { detail: { side } }))
  } catch {
    // ignore (very old browsers without CustomEvent constructor)
  }
}

function subscribeWidgetDockSide(onChange: () => void): () => void {
  const onStorage = (e: StorageEvent) => {
    if (e.key === WIDGET_DOCK_STORAGE_KEY) onChange()
  }
  window.addEventListener(WIDGET_DOCK_CHANGE_EVENT, onChange)
  window.addEventListener('storage', onStorage)
  return () => {
    window.removeEventListener(WIDGET_DOCK_CHANGE_EVENT, onChange)
    window.removeEventListener('storage', onStorage)
  }
}

/**
 * Shared widget dock preference. Every consumer (widget, pills, call modals) re-renders
 * when the side changes, in this tab or another one.
 */
export function useWidgetDockSide(): WidgetDockSide {
  return useSyncExternalStore(subscribeWidgetDockSide, readWidgetDockSide, () => 'right')
}
